// Libraries
import React from 'react';

// Supermove
import {Styled, Space, Icon} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ModalType, MutationError, useToast, useToggle} from '@supermove/hooks';
import {StageModel, Stage} from '@supermove/models';

// App
import FieldValue from '@shared/design/components/Field/FieldValue';
import EditPanel from '@shared/design/components/Panel/EditPanel';
import Text from '@shared/design/components/Text';
import SuccessToast from '@shared/design/components/Toast/SuccessToast';
import StageForm from '@shared/modules/Project/forms/StageForm';
import useUpdateStageMutation from '@shared/modules/Project/hooks/useUpdateStageMutation';
import ArchiveStageModal from '@shared/modules/Sales/components/ArchiveStageModal';
import EditStageFields from '@shared/modules/Sales/components/EditStageFields';
import RestoreStageModal from '@shared/modules/Sales/components/RestoreStageModal';

const Container = Styled.View`
`;

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
`;

const StagePanelBody = ({stage}: {stage: StageModel}) => {
  return (
    <React.Fragment>
      <FieldValue label={'Name'} value={stage.name} isResponsive />
      <Space height={16} />
      <FieldValue label={'Description'} value={stage.description} isResponsive empty={'None'} />
      <Space height={16} />
      <FieldValue.LabelText isResponsive>Color</FieldValue.LabelText>
      <Space height={4} />
      <Row>
        <Icon source={Icon.Circle} size={8} color={stage.color} />
        <Space width={6} />
        <Text.Body>{Stage.getColorNameFromValue(stage.color)}</Text.Body>
      </Row>
    </React.Fragment>
  );
};

const EditStagePanel = ({stage}: {stage: StageModel}) => {
  const updateStageSuccessToast = useToast({
    ToastComponent: SuccessToast,
    message: 'Changes saved.',
  });
  const editStageSettingsToggle = useToggle();
  const stageForm = StageForm.edit(stage);
  const {form, handleSubmit, submitting} = useUpdateStageMutation({
    stageForm,
    onSuccess: () => {
      editStageSettingsToggle.handleToggleOff();
      updateStageSuccessToast.handleToast();
    },
    onError: (errors: MutationError) => {
      console.log({errors});
    },
  });

  return (
    <EditPanel
      BodyComponent={StagePanelBody}
      bodyComponentProps={{stage}}
      EditBodyComponent={EditStageFields}
      editBodyComponentProps={{form}}
      title={'Overview'}
      handleSave={handleSubmit}
      isSubmitting={submitting}
      handleCancel={editStageSettingsToggle.handleToggleOff}
      isEditing={editStageSettingsToggle.isOn}
      handleEdit={editStageSettingsToggle.handleToggleOn}
      handleClose={editStageSettingsToggle.handleToggleOff}
    />
  );
};

const StagePageContent = ({
  stage,
  refetch,
  archiveStageModal,
  restoreStageModal,
}: {
  stage: StageModel;
  refetch: () => void;
  archiveStageModal: ModalType;
  restoreStageModal: ModalType;
}) => {
  return (
    <Container>
      <EditStagePanel stage={stage} />
      <ArchiveStageModal
        stageUuid={stage.uuid}
        isOpen={archiveStageModal.isOpen}
        handleClose={archiveStageModal.handleClose}
        refetch={refetch}
      />
      <RestoreStageModal
        stage={stage}
        isOpen={restoreStageModal.isOpen}
        handleClose={restoreStageModal.handleClose}
        refetch={refetch}
      />
    </Container>
  );
};

StagePageContent.fragment = gql`
  ${StageForm.edit.fragment}
  ${RestoreStageModal.fragment}

  fragment StagePageContent on Stage {
    id
    uuid
    name
    color
    description
    ...StageForm_edit
    ...RestoreStageModal
  }
`;

export default StagePageContent;
